import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Download, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CONTACT } from "../data/content";

export default function Footer() {
  const { tr, lang, isAr } = useLang();
  const year = new Date().getFullYear();

  const links = [
    { to: "/", label: tr.nav.home },
    { to: "/about", label: tr.nav.about },
    { to: "/products", label: tr.nav.products },
    { to: "/vision", label: tr.nav.vision },
    { to: "/contact", label: tr.nav.contact },
  ];

  return (
    <footer data-testid="main-footer" className="relative bg-ink text-white">
      <div className="grain absolute inset-0 opacity-[0.05] pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="ICON" className="h-12 w-12 object-contain" />
              <div>
                <p className="font-grotesk text-2xl font-700">ICON TRADING CO.</p>
                <p className="text-xs tracking-ultra uppercase text-brand-blue-light">{tr.footer.tagline}</p>
              </div>
            </div>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">{tr.footer.desc}</p>
            <a
              href={CONTACT.catalogue}
              download
              data-testid="footer-download-catalogue"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-600 text-ink transition-all hover:bg-brand-blue hover:text-white"
            >
              <Download className="h-4 w-4" />
              {tr.footer.download}
            </a>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs font-700 tracking-ultra uppercase text-white/40">{tr.footer.explore}</h4>
            <ul className="mt-5 space-y-3">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="group inline-flex items-center gap-1 text-sm text-white/70 transition-colors hover:text-white">
                    <span>{l.label}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-xs font-700 tracking-ultra uppercase text-white/40">{tr.footer.reach}</h4>
            <ul className="mt-5 space-y-4 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                <span>{isAr ? CONTACT.addressAr : CONTACT.addressEn}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 shrink-0 text-brand-red" />
                <a href={`tel:${CONTACT.phones[0].replace(/\s/g, "")}`} dir="ltr" className="hover:text-white">{CONTACT.phones[0]}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-brand-red" />
                <a href={`mailto:${CONTACT.emailPrimary}`} className="hover:text-white break-all">{CONTACT.emailPrimary}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-brand-red" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-white break-all">{CONTACT.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-xs text-white/40">© {year} ICON Trading Co. {tr.footer.rights}</p>
          <p className="text-xs tracking-ultra uppercase text-white/40">{CONTACT.website}</p>
        </div>
      </div>
    </footer>
  );
}
